/* eslint import/no-webpack-loader-syntax: off */
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from 'react-router-dom';
import mapboxgl from '!mapbox-gl';
import * as turf from '@turf/turf';

import { useErrorHandler } from "../../../../_metronic/helpers/ErrorHandler";
import { mapState } from "./MapState";
import { drawState } from "./Draw/DrawState";
import * as GeoJsonService from "../services/GeoJsonService";
import { formatHectares } from "../../../shared/numberFormat";
import "./MapLayersV2.style.scss";
 
const MapLayersV2Context = createContext();

export function useMapLayersV2() {
  return useContext(MapLayersV2Context);
}

export const MapLayersV2Consumer = MapLayersV2Context.Consumer;

const getSafeExternalUrl = (value) => {
    try {
        const url = new URL(String(value || ''));
        return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch (error) {
        return null;
    }
};

const createFeaturePopup = ({
    title,
    rows = [],
    sections = [],
    links = [],
    linksTitle = '',
}) => {
    const container = document.createElement('div');
    container.className = 'gis-feature-popup';

    const heading = document.createElement('h4');
    heading.className = 'gis-feature-popup__title';
    heading.textContent = title || '';
    container.appendChild(heading);

    const appendRows = (target, items = []) => {
        items.filter((row) => row?.value != null && row.value !== '').forEach((row) => {
            const item = document.createElement('div');
            item.className = [
                'gis-feature-popup__row',
                row.fullWidth ? 'gis-feature-popup__row--full' : '',
            ].filter(Boolean).join(' ');

            const label = document.createElement('span');
            label.className = 'gis-feature-popup__label';
            label.textContent = row.label || '';

            const value = document.createElement('strong');
            value.className = 'gis-feature-popup__value';
            value.textContent = String(row.value);

            item.appendChild(label);
            item.appendChild(value);
            target.appendChild(item);
        });
    };

    appendRows(container, rows);

    sections.forEach((section) => {
        const sectionRows = (section?.rows || []).filter(
            (row) => row?.value != null && row.value !== ''
        );
        if (!sectionRows.length && !section?.text) {
            return;
        }

        const sectionElement = document.createElement('section');
        sectionElement.className = 'gis-feature-popup__section';

        if (section.title) {
            const sectionTitle = document.createElement('h5');
            sectionTitle.className = 'gis-feature-popup__section-title';
            sectionTitle.textContent = section.title;
            sectionElement.appendChild(sectionTitle);
        }

        if (section.text) {
            const text = document.createElement('p');
            text.className = 'gis-feature-popup__text';
            text.textContent = section.text;
            sectionElement.appendChild(text);
        }

        appendRows(sectionElement, sectionRows);
        container.appendChild(sectionElement);
    });

    const safeLinks = links
        .map((link) => ({...link, href: getSafeExternalUrl(link?.href)}))
        .filter((link) => link.href && link.label);

    if (safeLinks.length) {
        const linksSection = document.createElement('section');
        linksSection.className = 'gis-feature-popup__section gis-feature-popup__links';

        if (linksTitle) {
            const sectionTitle = document.createElement('h5');
            sectionTitle.className = 'gis-feature-popup__section-title';
            sectionTitle.textContent = linksTitle;
            linksSection.appendChild(sectionTitle);
        }

        safeLinks.forEach((link) => {
            const anchor = document.createElement('a');
            anchor.className = 'gis-feature-popup__link';
            anchor.href = link.href;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            anchor.referrerPolicy = 'no-referrer';
            anchor.textContent = link.label;
            const preventMiddleClick = (event) => {
                if (event.button === 1) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            };
            anchor.addEventListener('mousedown', preventMiddleClick);
            anchor.addEventListener('auxclick', preventMiddleClick);
            linksSection.appendChild(anchor);
        });
        container.appendChild(linksSection);
    }

    return container;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const createFloatingFeaturePopup = ({ content, lngLat, map, onRemove }) => {
    const popupElement = document.createElement('aside');
    popupElement.className = 'gis-floating-feature-popup';
    popupElement.setAttribute('role', 'dialog');

    const closeButton = document.createElement('button');
    closeButton.className = 'gis-floating-feature-popup__close';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Cerrar');
    closeButton.textContent = '×';
    popupElement.appendChild(closeButton);
    popupElement.appendChild(createFeaturePopup(content));
    document.body.appendChild(popupElement);

    let removed = false;
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            remove();
        }
    };
    const remove = () => {
        if (removed) {
            return;
        }

        removed = true;
        map.off('movestart', remove);
        map.off('zoomstart', remove);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('resize', position);
        popupElement.remove();
        onRemove?.();
    };
    const position = () => {
        if (removed) {
            return;
        }

        const mapRect = map.getContainer().getBoundingClientRect();
        const point = map.project(lngLat);
        const anchorX = mapRect.left + point.x;
        const anchorY = mapRect.top + point.y;
        const margin = 12;
        const viewportBottom = Math.min(mapRect.bottom - margin, window.innerHeight - margin);
        const viewportTop = Math.max(mapRect.top + margin, margin);
        const viewportLeft = Math.max(mapRect.left + margin, margin);
        const viewportRight = Math.min(mapRect.right - margin, window.innerWidth - margin);
        const legendRect = document.querySelector('.gis-desktop-legend')?.getBoundingClientRect();
        const panelRect = document.querySelector('.right-gis-container')?.getBoundingClientRect();
        const desktop = window.innerWidth > 700;
        const safeRight = desktop && panelRect?.width
            ? Math.min(viewportRight, panelRect.left - margin)
            : viewportRight;
        const safeBottom = desktop && legendRect?.height
            ? Math.min(viewportBottom, legendRect.top - margin)
            : viewportBottom;
        const safeWidth = safeRight - viewportLeft;
        const safeHeight = safeBottom - viewportTop;
        const useDesktopSafeArea = desktop && safeWidth >= 280 && safeHeight >= 220;
        const availableHeight = useDesktopSafeArea
            ? safeHeight
            : viewportBottom - viewportTop;

        popupElement.style.maxHeight = `${Math.min(420, availableHeight)}px`;

        const popupRect = popupElement.getBoundingClientRect();
        const rightBoundary = useDesktopSafeArea ? safeRight : viewportRight;
        const bottomBoundary = useDesktopSafeArea ? safeBottom : viewportBottom;
        const left = clamp(
            anchorX - popupRect.width / 2,
            viewportLeft,
            Math.max(viewportLeft, rightBoundary - popupRect.width)
        );
        const top = clamp(
            anchorY - popupRect.height / 2,
            viewportTop,
            Math.max(viewportTop, bottomBoundary - popupRect.height)
        );

        popupElement.style.left = `${Math.round(left)}px`;
        popupElement.style.top = `${Math.round(top)}px`;
    };

    closeButton.addEventListener('click', remove);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', position);
    map.on('movestart', remove);
    map.on('zoomstart', remove);
    window.requestAnimationFrame(position);

    return { remove };
};

export function MapLayersV2Provider({ children }) {
    const { errorHandler } = useErrorHandler();
    const location = useLocation();
    // Replacing the same URL or normalizing its image-section query keeps camera ownership.
    const fitScope = useMemo(() => ({}), [location.pathname]);
    const currentFitScope = useRef(fitScope);
    currentFitScope.current = fitScope;
    const pendingFit = useRef(null);
    const mounted = useRef(true);
    const soilMapHandlers = useRef({});
    const soilMapPopup = useRef(null);
    const [ loading, setLoading ] = useState(false);

    useLayoutEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            pendingFit.current?.cancel();
        };
    }, []);

    useEffect(() => () => {
        if (pendingFit.current?.scope === fitScope) {
            pendingFit.current.cancel();
        }
    }, [fitScope]);

    const loadGeoJson = (callback) => {
        errorHandler(GeoJsonService.fetch()).then(res => {
            if (res.ok) {
                mapState.source().set(res.data);
                
                if (callback) {
                    callback(mapState);
                }
            }
        });
    };
   
    const init = (map) => {
        mapState.reload();
        mapState.setMap(map);
        loadGeoJson((updatedMapState) => {
            updatedMapState.layers().init();
            updatedMapState.markers().init();
            updatedMapState.markers().handleZoom();
            drawState.init(updatedMapState, map);
            setMapEvents(map);
        });
    };
    
    const reloadSource = (callback) => {
        loadGeoJson(() => {
            mapState.markers().reload();
            if (typeof callback == "function") {
                callback();
            }
        });
    }

    const setMapEvents = (map) => {
        map.on('zoom', mapState.markers().handleZoom());
    }

    const getFitBufferDistance = () => {
        if (typeof window === "undefined") {
            return 0.5;
        }

        if (window.innerWidth <= 700) {
            const hasFloatingLegend = document.getElementById('kt_right_mobile_legend') instanceof HTMLElement;
            return hasFloatingLegend ? 0.12 : 0.2;
        }

        return 0.5;
    };

    const requestFit = (apply) => {
        if (!mounted.current || currentFitScope.current !== fitScope) {
            return () => {};
        }

        // Camera requests are latest-wins; unrelated layer operations still use wait().
        pendingFit.current?.cancel();
        const request = { scope: fitScope, timer: null, cancel: null };
        request.cancel = () => {
            window.clearTimeout(request.timer);
            if (pendingFit.current === request) {
                pendingFit.current = null;
            }
        };
        pendingFit.current = request;
        const attempt = () => {
            if (!mounted.current || currentFitScope.current !== fitScope || pendingFit.current !== request) {
                request.cancel();
                return;
            }
            if (!mapState.isInitialized()) {
                request.timer = window.setTimeout(attempt, 100);
                return;
            }
            pendingFit.current = null;
            apply();
        };
        attempt();
        return request.cancel;
    };

    const fit = (id) => {
        return requestFit(() => {
            let feature = mapState.source().getFeature(id);
            if (feature == null) {
                return;
            }

            feature = feature["geometry"];

            mapState.fit(turf.buffer(feature, getFitBufferDistance(), {
                units: "kilometers"
            }));
        });
    };

    const fitToPolygon = (polygon) => {
        return requestFit(() => {
            mapState.fit(turf.buffer(polygon, getFitBufferDistance(), {
                units: "kilometers"
            }));
        });
    };


    const wait = (f) => {
        if (!mapState.isInitialized()) {
            return setTimeout(() => wait(f), 100);
        }

        return f();
    };

    const land = {
        _unfill: (f) => {
            f["properties"].opacity = 0;
            f["properties"].borderOpacity = 1;
            return f;
        },
        unfill: (id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.id == id, land._unfill));
        },
        unfillByFieldId: (field_id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.field_id == field_id, land._unfill));
        },
        _fill: (f) => {
            f["properties"].opacity = 0.5;
            f["properties"].borderOpacity = 1;
            return f;
        },
        fill: (id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.id == id, land._fill));
        },
        fillByFieldId: (field_id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.field_id == field_id, land._fill));
        },
        hide: (id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.id == id, (f) => {
                f["properties"].opacity = 0;
                f["properties"].borderOpacity = 0;
                return f;
            }));
        },
        addImage: ({ landId, imageType, imageUrl, polygon, bbox, layerId, opacity, resampling }) => {
            wait(() => {
                const layer = layerId || `${imageType}-${landId}`;
                mapState.layers().addImage(layer, imageUrl, polygon, bbox, { opacity, resampling });
            });
        },
        addCanvas: ({ layerId, canvas, bbox, opacity }) => {
            wait(() => {
                mapState.layers().addCanvas(layerId, canvas, bbox, { opacity });
            });
        },
        setImageVisibility: ({ landId, imageType, visible, layerId }) => {
            wait(() => {
                mapState.layers().setLayerVisibility(layerId || `${imageType}-${landId}`, visible ? 'visible' : 'none');
            });
        },
        setImageOpacity: ({ landId, imageType, layerId, opacity }) => {
            wait(() => {
                mapState.layers().setRasterOpacity(layerId || `${imageType}-${landId}`, opacity);
            });
        },
        deleteImage: ({ landId, imageType, layerId }) => {
            wait(() => {
                mapState.layers().deleteLayerAndSource(layerId || `${imageType}-${landId}`)
            });
        },
        addLine: ({ layerId, coordinates, color, width }) => {
            wait(() => {
                mapState.layers().addLine({ layerId, coordinates, color, width });
            });
        },
        deleteLayer: ({ layerId }) => {
            wait(() => {
                mapState.layers().deleteLayerAndSource(layerId);
            });
        },
        marker: {
            hide: (id) => {
                wait(() => mapState.markers().hide(id));
            },
            show: (id) => {
                wait(() => mapState.markers().show(id));
            }
        },
        ambient: {
            _onClickEvent: (e) => {
                const supHa = e.features[0].properties.sup_ha;
                const { lat, lng } = e.lngLat;
                new mapboxgl.Popup()
                    .setLngLat([lng, lat])
                    .setHTML(`Superficie: ${formatHectares(supHa)}`)
                    .addTo(mapState.map);
            },
            add: ({ landId, geojson, layerId, opacity, interactive = true }) => {
                const layer = mapState.layers().addAmbient({ landId, geojson, layerId, opacity });
                if (interactive) {
                    mapState.map.on('click', layer, land.ambient._onClickEvent);
                }
            },
            remove: ({ landId, layerId }) => {
                const layer = mapState.layers().removeAmbient({ landId, layerId });
                mapState.map.off('click', layer, land.ambient._onClickEvent);
            }
        },
        soilMap: {
            add: ({ landId, geojson, layerId, opacity, getPopupContent, fit = false }) => {
                wait(() => {
                    const layer = layerId || `soil-map-${landId}`;
                    const previousHandlers = soilMapHandlers.current[layer];

                    if (previousHandlers) {
                        mapState.map.off('click', layer, previousHandlers.click);
                        mapState.map.off('mouseenter', layer, previousHandlers.mouseenter);
                        mapState.map.off('mouseleave', layer, previousHandlers.mouseleave);
                        if (previousHandlers.fit) {
                            mapState.map.off('idle', previousHandlers.fit);
                        }
                    }

                    mapState.layers().addSoilMap({ landId, geojson, layerId: layer, opacity });
                    let fitMap = null;
                    if (fit) {
                        fitMap = () => {
                            mapState.fit(turf.buffer(geojson, getFitBufferDistance(), {
                                units: 'kilometers'
                            }));
                        };
                        fitMap();
                        mapState.map.once('idle', fitMap);
                    }

                    const click = (event) => {
                        const feature = event.features?.[0];
                        if (!feature) {
                            return;
                        }

                        const content = getPopupContent?.(feature.properties || {}) || {};
                        soilMapPopup.current?.remove();

                        const popup = createFloatingFeaturePopup({
                            content,
                            lngLat: event.lngLat,
                            map: mapState.map,
                            onRemove: () => {
                                if (soilMapPopup.current === popup) {
                                    soilMapPopup.current = null;
                                }
                            },
                        });
                        soilMapPopup.current = popup;
                    };
                    const mouseenter = () => {
                        mapState.map.getCanvas().style.cursor = 'pointer';
                    };
                    const mouseleave = () => {
                        mapState.map.getCanvas().style.cursor = '';
                    };

                    mapState.map.on('click', layer, click);
                    mapState.map.on('mouseenter', layer, mouseenter);
                    mapState.map.on('mouseleave', layer, mouseleave);
                    soilMapHandlers.current[layer] = { click, mouseenter, mouseleave, fit: fitMap };
                });
            },
            remove: ({ landId, layerId }) => {
                wait(() => {
                    const layer = layerId || `soil-map-${landId}`;
                    const handlers = soilMapHandlers.current[layer];

                    if (handlers) {
                        mapState.map.off('click', layer, handlers.click);
                        mapState.map.off('mouseenter', layer, handlers.mouseenter);
                        mapState.map.off('mouseleave', layer, handlers.mouseleave);
                        if (handlers.fit) {
                            mapState.map.off('idle', handlers.fit);
                        }
                        delete soilMapHandlers.current[layer];
                    }

                    soilMapPopup.current?.remove();
                    soilMapPopup.current = null;
                    mapState.layers().removeSoilMap({ landId, layerId: layer });
                });
            }
        }
    };

    const field = {
        hide: (id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.id == id, (f) => {
                f["properties"].borderOpacity = 0;
                return f;
            }));
        },
        show: (id) => {
            wait(() => mapState.source().updateFeature((f) => f.properties.id == id, (f) => {
                f["properties"].borderOpacity = 1;
                return f;
            }));
        },
    }

    const on = (event, layer, callback) => {
        wait(() => {
            if (callback) {
                mapState.map.on(event, layer, callback(mapState.map)); 
            } else {
                mapState.map.on(event, layer(mapState.map));
            }
        });
    }

    const value = {
        init,
        reloadSource,
        fit,
        fitToPolygon,
        land,
        field,
        on,
        loading, setLoading
    };

    return (
        <>
            <MapLayersV2Context.Provider value={value}>
                {children}
            </MapLayersV2Context.Provider>
        </>
    )
};
